// MessageNew — single-page form to compose a message to learners.
//
// Fields:
//   - 訊息    : <textarea>     (Stage 9: optional if photo attached)
//   - 照片    : 一張 jpg/png   (Stage 9: optional if text provided)
//   - 收件人  : radio(全體 / 指定) + 14-learner checkbox list
//
// Form state is plain useState (no react-hook-form — overkill for
// three fields). validation is inline on submit.
//
// Stage 9 write flow (2 steps when photo attached):
//   1. setDoc with text (or "(附照片)" placeholder) — fires CF push
//      so learner gets notified immediately, even before upload finishes.
//   2. uploadMessagePhoto + updateDoc with photoUrl/photoStoragePath —
//      listener on learner side picks up the patch and renders the image.
//   Upload failure: surface a UI error and stamp "(照片上傳失敗)" onto
//   the doc text so the learner can see why the attachment didn't appear.

import {
  Hairline,
  Han,
  HUD,
  MonoLabel,
  T,
  TextBtn,
} from "@goodbye/ui";
import { messageDoc, messagesCol } from "@goodbye/firebase";
import {
  Timestamp,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdminAuthCtx, useAdminSyncCtx } from "../context";
import { uploadMessagePhoto } from "../lib/uploadMessagePhoto";

export function MessageNew() {
  const navigate = useNavigate();
  const { email } = useAdminAuthCtx();
  const { users, messages } = useAdminSyncCtx();
  // 2026-05-30:把 worker(u15-u23)加回收訊對象清單(原本濾掉)。下游
  // notifyMessageCreated 對 audienceUserIds 不分 role、worker 也有 fcmToken,
  // 加回來功能正常。變數名沿用 `learners`(全選/map 都吃它),語意上現在是
  // 「所有可收訊對象」。users 已 by no 排序,worker 自然排在學員後面。
  const learners = useMemo(() => users, [users]);

  // ?from=<messageId> — "resend retracted" flow: prefill text + audience
  // from a previously retracted message. Worker can tweak then send;
  // we ALWAYS create a new doc, never mutate the original.
  const [searchParams] = useSearchParams();
  const fromId = searchParams.get("from");
  const sourceMessage = fromId
    ? messages.find((m) => m.id === fromId)
    : null;

  const [text, setText] = useState("");
  const [broadcast, setBroadcast] = useState(true);
  const [audience, setAudience] = useState<Set<string>>(new Set());
  // Stage 9: optional attached photo. preview URL is created lazily from
  // photoFile so we can revoke it cleanly when the file changes or unmounts.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreview(null);
      return;
    }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  // Prefill once when the source message resolves. messagesLoading races
  // the route mount, so we wait until the doc actually appears.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    if (!sourceMessage) return;
    prefilledRef.current = true;
    setText(sourceMessage.text ?? "");
    setBroadcast(sourceMessage.broadcast);
    setAudience(new Set(sourceMessage.audienceUserIds));
  }, [sourceMessage]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string } | null>(null);

  function toggleAudience(userId: string) {
    setAudience((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function onSubmit() {
    setError(null);
    const trimmedText = text.trim();
    if (!trimmedText && !photoFile) {
      setError("訊息或照片請至少填一個");
      return;
    }
    if (!broadcast && audience.size === 0) {
      setError("請至少選一位收件人或切回全體");
      return;
    }
    setSubmitting(true);
    try {
      const ref = doc(messagesCol());
      // 1st write: doc body + push trigger. If no text, fall back to
      // 「(附照片)」so the lock-screen notification body is meaningful.
      const initialText = trimmedText || "(附照片)";
      await setDoc(ref, {
        text: initialText,
        broadcast,
        audienceUserIds: broadcast ? [] : Array.from(audience),
        createdBy: email ?? "unknown",
        createdAt: serverTimestamp() as unknown as Timestamp,
      });

      // 2nd write: upload photo (if any) and patch the doc with the
      // download URL. The listener on the learner side picks this up
      // and renders the image automatically.
      if (photoFile) {
        try {
          const { photoUrl, photoStoragePath } = await uploadMessagePhoto({
            messageId: ref.id,
            file: photoFile,
          });
          await updateDoc(messageDoc(ref.id), {
            photoUrl,
            photoStoragePath,
          });
        } catch (uploadErr) {
          console.error("[MessageNew] photo upload failed", uploadErr);
          // Stamp the doc so the learner sees why the photo's missing.
          // Don't delete — the learner already got the push.
          await updateDoc(messageDoc(ref.id), {
            text: `${initialText}\n(照片上傳失敗)`,
          }).catch(() => undefined);
          setError(
            "訊息已送出,但照片上傳失敗。請到列表撤回後重新發一次。",
          );
          setSubmitting(false);
          return;
        }
      }

      setDone({ id: ref.id });
    } catch (err) {
      console.error("[MessageNew] submit failed", err);
      setError(err instanceof Error ? err.message : "送出失敗");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: T.paper,
          paddingBottom: "calc(140px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <HUD label="工 作 端" />
        <div
          style={{
            padding: "40px 32px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <Han size={22} color={T.ink900}>已送出</Han>
          <MonoLabel size={12} color={T.ink500}>
            訊 息 編 號 · {done.id}
          </MonoLabel>
          <Hairline />
          <Han size={14} color={T.ink500} lh={1.9}>
            訊息已寄出。學員手機會收到推播通知。
          </Han>
          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
            <TextBtn
              size={14}
              color={T.ink900}
              onClick={() => navigate("/messages")}
              style={{ padding: "10px 14px" }}
            >
              · 回 列 表
            </TextBtn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
        // 140 (AdminNav + base) + ~50 (fixed submit bar above nav).
        // Without the extra room, the last audience checkbox sits behind
        // the submit bar on a short screen.
        paddingBottom: "calc(190px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <HUD label="工 作 端" />
      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Han size={22} color={T.ink900}>新 訊 息</Han>
          <TextBtn
            size={12}
            color={T.ink350}
            framed={false}
            onClick={() => navigate("/messages")}
          >
            · 返回
          </TextBtn>
        </div>
      </div>
      <Hairline style={{ margin: "8px 32px 0" }} />

      <div
        style={{
          padding: "20px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          flex: 1,
        }}
      >
        {/* Body */}
        <div>
          <MonoLabel
            size={11}
            color={T.ink500}
            style={{ display: "block", marginBottom: 4 }}
          >
            訊 息
          </MonoLabel>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="例:今天集合提醒(或只附一張照片)"
            rows={6}
            style={{
              width: "100%",
              fontFamily: T.han,
              fontSize: 15,
              lineHeight: 1.7,
              color: T.ink700,
              background: "transparent",
              border: "none",
              borderBottom: `1px solid ${T.ink200}`,
              padding: "8px 0",
              outline: "none",
              resize: "vertical",
            }}
          />
        </div>

        {/* Photo (Stage 9, optional) */}
        <div>
          <MonoLabel
            size={11}
            color={T.ink500}
            style={{ display: "block", marginBottom: 8 }}
          >
            照 片 (選 填)
          </MonoLabel>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setPhotoFile(f);
              // Reset the input value so picking the same file twice
              // (after a Remove) still fires onChange.
              if (e.target) e.target.value = "";
            }}
          />
          {photoPreview ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <img
                src={photoPreview}
                alt="附加照片預覽"
                style={{
                  width: "min(220px, 60vw)",
                  aspectRatio: "1 / 1",
                  objectFit: "cover",
                  border: `1px solid ${T.ink200}`,
                }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <TextBtn
                  size={12}
                  color={T.ink900}
                  ls="0.12em"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: "6px 12px",
                    border: `1px solid ${T.ink200}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  · 換 照 片
                </TextBtn>
                <TextBtn
                  size={12}
                  color={T.ink500}
                  ls="0.12em"
                  onClick={() => setPhotoFile(null)}
                  style={{
                    padding: "6px 12px",
                    border: `1px solid ${T.ink200}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  · 移 除
                </TextBtn>
              </div>
            </div>
          ) : (
            <TextBtn
              size={12}
              color={T.ink900}
              ls="0.12em"
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: "8px 14px",
                border: `1px solid ${T.ink200}`,
                whiteSpace: "nowrap",
              }}
            >
              · 加 照 片
            </TextBtn>
          )}
        </div>

        {/* Audience */}
        <div>
          <MonoLabel
            size={11}
            color={T.ink500}
            style={{ display: "block", marginBottom: 8 }}
          >
            收 件 人
          </MonoLabel>
          <div
            style={{
              display: "flex",
              gap: 14,
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            <AudienceRadio
              active={broadcast}
              label="全 體"
              onClick={() => setBroadcast(true)}
            />
            <AudienceRadio
              active={!broadcast}
              label="指 定"
              onClick={() => setBroadcast(false)}
            />
          </div>
          {!broadcast && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                marginTop: 6,
                // Stage 6: keep learner picker inside a scrollable region
                // so the submit button (fixed below) is always reachable
                // — even with the on-screen keyboard up on a small phone.
                maxHeight: "min(280px, 32dvh)",
                overflowY: "auto",
                borderTop: `1px solid ${T.ink100}`,
                borderBottom: `1px solid ${T.ink100}`,
              }}
            >
              {learners.length === 0 && (
                <Han size={13} color={T.ink500} lh={1.9}>
                  找不到學員名單。
                </Han>
              )}
              {learners.map((u) => {
                const checked = audience.has(u.id);
                return (
                  <label
                    key={u.id}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 12,
                      padding: "10px 0",
                      borderBottom: `1px solid ${T.ink100}`,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAudience(u.id)}
                      style={{ accentColor: T.ink900 }}
                    />
                    <MonoLabel size={11} color={T.ink500}>
                      {u.no}
                    </MonoLabel>
                    <Han size={14} color={T.ink900}>
                      {u.name}
                    </Han>
                  </label>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* Stage 6: Submit bar fixed above AdminNav so a long audience
          list never pushes 「· 送 出」 off-screen. AdminNav height ≈
          96px (10 + 12 + 32 + 44 — see _AdminNav.tsx) + safe-area. */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
          background: T.paper,
          borderTop: `1px solid ${T.ink100}`,
          padding: "12px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          alignItems: "stretch",
          zIndex: 11,
        }}
      >
        {error && (
          <Han size={13} color={T.ink900} lh={1.7}>
            ⚠ {error}
          </Han>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <TextBtn
            size={14}
            color={
              (text.trim() || photoFile) && !submitting ? T.ink900 : T.ink350
            }
            ls="0.12em"
            onClick={onSubmit}
            disabled={(!text.trim() && !photoFile) || submitting}
            style={{
              padding: "10px 18px",
              border: `1px solid ${T.ink200}`,
              whiteSpace: "nowrap",
            }}
          >
            {submitting ? "· 送 出 中…" : "· 送 出"}
          </TextBtn>
        </div>
      </div>

    </div>
  );
}

function AudienceRadio({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        padding: "10px 0",
        borderBottom: `1px solid ${active ? T.ink900 : T.ink100}`,
        cursor: "pointer",
        fontFamily: T.han,
        fontSize: 14,
        color: active ? T.ink900 : T.ink350,
        letterSpacing: "0.18em",
      }}
    >
      {active ? "· " : "  "}
      {label}
    </button>
  );
}
